import { ResourceAvailabilityMetaValue, type ResourceAvailabilityView, type TaskGanttView } from '@nexelem/graphne-api';
import clsx from 'clsx';
import { useCustomTranslation } from '../../../../hooks';
import { useFormatTaskDuration } from '../Gantt/hooks';

type WorkerAvailabilityProps = {
  availabilities?: ResourceAvailabilityView[];
  className?: string;
  node?: TaskGanttView;
};

type Availability = {
  startDate: Date;
  endDate: Date;
  availability: ResourceAvailabilityMetaValue;
};

const availabilityColorMap: Record<ResourceAvailabilityMetaValue, string> = {
  [ResourceAvailabilityMetaValue.Available]: 'text-text-status-green-darker',
  [ResourceAvailabilityMetaValue.Unavailable]: 'text-text-primary',
  [ResourceAvailabilityMetaValue.PartiallyAvailable]: 'text-text-status-orange',
};

const availabilityTranslationKeyMap: Record<
  ResourceAvailabilityMetaValue,
  'available' | 'unavailable' | 'partiallyAvailable'
> = {
  [ResourceAvailabilityMetaValue.Available]: 'available',
  [ResourceAvailabilityMetaValue.Unavailable]: 'unavailable',
  [ResourceAvailabilityMetaValue.PartiallyAvailable]: 'partiallyAvailable',
};

export const WorkerAvailability = ({ className, availabilities, node }: WorkerAvailabilityProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan');
  const formatTaskDurationNoDuration = useFormatTaskDuration({ withoutDuration: true });
  const formatTaskDuration = useFormatTaskDuration({ compact: true });

  const availabilitiesMapped = availabilities
    ?.map(({ range, availability }) => {
      return {
        availability,
        startDate: range.from && new Date(range.from),
        endDate: range.to && new Date(range.to),
      };
    })
    .filter((a): a is Availability => !!a.startDate && !!a.endDate);

  const header = node
    ? tWrapper('availabilityDuringTask', {
        duration: formatTaskDurationNoDuration({ startDate: new Date(node.start), endDate: new Date(node.end) }),
      })
    : tWrapper('availability');

  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      <div className="text-t14-sb text-text-secondary-darker">{header}</div>
      {!availabilitiesMapped?.length && <div>-</div>}
      {availabilitiesMapped?.map(({ startDate, endDate, availability }, index) => (
        <div className={clsx('text-s16', availabilityColorMap[availability])} key={index}>
          {formatTaskDuration({ startDate, endDate })} - {tWrapper(availabilityTranslationKeyMap[availability])}
        </div>
      )) || '-'}
    </div>
  );
};
